import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Accordion } from '@/components/Accordion';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { ScrollableModal } from '@/feature/alerts/components/Modal';
import { BSFlatList } from '@/feature/alerts/components/utils';
import { common } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

type HowToPlaySection = {
    title: string;
    image?: string;
    content?: React.ReactNode;
};

type Scoring = {
    action: string;
    points: string;
    isNegative: boolean;
};

export type HowToPlayContent = {
    howToPlay: HowToPlaySection[];
    scoring: Scoring[];
    howToMaster: HowToPlaySection[];
};

export type GameHowToPlayModalRef = {
    show: (content: HowToPlayContent) => void;
};

const ItemSeparator = () => <LineSeparator style={styles.itemSeparator} />;

const ScoringItemSeparator = () => <ItemSeparator />;

export const GameHowToPlayModal = React.forwardRef<GameHowToPlayModalRef, {}>((_props, ref) => {
    const modalRef = useRef<BottomSheetModal>(null);
    const [data, setData] = useState<HowToPlayContent>();
    const { t } = useTranslation('arcade_how_to_play');

    useImperativeHandle(ref, () => ({
        show: contentData => {
            setData(contentData);
            modalRef.current?.present();
        },
    }));

    const flatListData = useMemo(() => {
        if (!data) {
            return [];
        }

        return [
            ...data.howToPlay.map(item => ({
                title: item.title,
                content: (
                    <>
                        {item.image ? (
                            <Box borderRadius="r16" overflow="hidden" mb="s12">
                                <Image source={{ uri: item.image }} height={185} />
                            </Box>
                        ) : null}
                        {item.content}
                    </>
                ),
            })),
            {
                title: t('scoring'),
                content: (
                    <BSFlatList
                        data={data.scoring}
                        renderItem={({ item }) => (
                            <Row alignItems="center" justifyContent="space-between" height={56}>
                                <Text variant="bodyMedium2" color="neutral300">
                                    {item.action}
                                </Text>
                                <Text
                                    variant="bodyMedium2"
                                    color={item.isNegative ? ds.colorToken.text.danger : ds.colorToken.text.primary}
                                >
                                    {item.points}
                                </Text>
                            </Row>
                        )}
                        ItemSeparatorComponent={ScoringItemSeparator}
                        scrollEnabled={false}
                    />
                ),
            },
        ];
    }, [data, t]);

    const { bottom } = useSafeAreaInsets();

    return (
        <ScrollableModal
            header={
                <Column alignItems="center" height={44} justifyContent="center">
                    <Text variant="titleMedium2" color={ds.colorToken.text.primary}>
                        {t('how_to_play')}
                    </Text>
                </Column>
            }
            sheetRef={modalRef}
            id={'game-how-to-play-modal'}
        >
            {data ? (
                <Box px="s16">
                    <BSFlatList
                        contentContainerStyle={[styles.contentContainer, { paddingBottom: bottom + 24 }]}
                        data={flatListData}
                        renderItem={({ item, index }) => {
                            const isLast = index === flatListData.length - 1;
                            return (
                                <Accordion
                                    accordionStyle={{
                                        ...styles.accordionBase,
                                        ...(index === 0 && styles.accordionTop),
                                        ...(isLast && styles.accordionBottom),
                                    }}
                                    headerStyle={common.paddingHorizontal}
                                    header={
                                        <Text variant="bodyMedium2" color={ds.colorToken.text.primary}>
                                            {item.title}
                                        </Text>
                                    }
                                    content={
                                        <Column paddingHorizontal="s16" width="100%">
                                            {item.content}
                                        </Column>
                                    }
                                    contentStyle={styles.contentStyle}
                                />
                            );
                        }}
                        scrollEnabled={false}
                        ItemSeparatorComponent={ItemSeparator}
                        keyExtractor={item => 'section-' + item.title}
                        showsVerticalScrollIndicator={showScrollIndicator}
                    />
                </Box>
            ) : null}
        </ScrollableModal>
    );
});

const styles = StyleSheet.create({
    itemSeparator: {
        height: 1,
    },
    contentContainer: {
        paddingTop: 16,
    },
    accordionBase: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    accordionTop: {
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
    },
    accordionBottom: {
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
    },
    contentStyle: {
        paddingBottom: 16,
    },
});
